import api from './api';
import type { Insurer, InsurerEmployee, InsurerAPIConfig, PaginatedResponse } from '@/types/admin';

/** One of the broker's own insurers, as a dropdown option. */
export interface BrokerInsurerOption {
  id: string;
  name: string;
  code: string;
  short_name?: string;
  logo_url?: string;
  logo_display_url?: string;
}

/** A broker's relationship with one insurance company.
 *
 *  FOUR different numbers live on this row and they mean four different things:
 *  `available_policy_count` is the insurer's whole PRODUCT CATALOGUE (identical
 *  for every broker), `enabled_policy_count` is how many of those products this
 *  broker has switched on, and `sold_policy_count` / `active_policy_count` are
 *  this broker's OWN policies with that company -- the only two that are
 *  business rather than catalogue. */
export interface BrokerInsurerRow {
  id: string;
  name: string;
  code: string;
  /** U-03: `Insurer.effective_logo_url` -- `InsurerListSerializer` has always
   *  sent it on this payload (`/brokers/insurer-management/` serialises each
   *  row with it); the type simply never declared it. '' when there is none. */
  logo_display_url?: string;
  relation_status: string;
  available_policy_count?: number;
  enabled_policy_count?: number;
  sold_policy_count?: number;
  active_policy_count?: number;
  /** '' when this broker has never asked; otherwise pending/approved/declined. */
  connection_request_status?: string;
  [key: string]: unknown;
}

export interface InsurerConnectionRequest {
  id: string;
  broker_id: string;
  broker_name: string;
  broker_code: string;
  insurer_id: string;
  insurer_name: string;
  insurer_code: string;
  status: 'pending' | 'approved' | 'declined' | string;
  note: string;
  decline_reason: string;
  reviewed_by: string;
  reviewed_at: string | null;
  created_at: string | null;
}

export interface InsurersParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  category?: string;
}

/** #559: one insurer on the integration status dashboard. */
export interface IntegrationStatusLine {
  line: string;
  /** Always "code" today: the matrix shows what is implemented, not what is live (#554). */
  state: string;
  operations: Record<string, boolean>;
}

export interface IntegrationStatusInsurer {
  id: string;
  name: string;
  code: string;
  category: string;
  status: string;
  api_status: { state: string; label: string; last_tested_at: string | null };
  has_adapter: boolean;
  /** Plug-in code serving this row (differs from `code` for aliased rows). */
  adapter_code?: string;
  lines: IntegrationStatusLine[];
  drive_code: string;
  expected_lines: string[];
  expected_lines_basis: string;
  note: string;
}

export interface IntegrationStatusOrphanPlugin {
  code: string;
  name: string;
  lines: string[];
}

export interface IntegrationStatusResponse {
  /** Plug-ins in code with no insurer record (R2-F10). */
  plugins_without_insurer?: IntegrationStatusOrphanPlugin[];
  operations: string[];
  not_in_base: string[];
  summary: Record<string, number>;
  insurers: IntegrationStatusInsurer[];
}

export const insurersService = {
  async integrationStatus(): Promise<IntegrationStatusResponse> {
    const response = await api.get('/insurers/insurers/integration-status/');
    return response.data;
  },

  // Insurers
  async list(params?: InsurersParams): Promise<PaginatedResponse<Insurer>> {
    const response = await api.get('/insurers/insurers/', { params });
    return response.data;
  },

  async get(id: string): Promise<Insurer> {
    const response = await api.get(`/insurers/insurers/${id}/`);
    return response.data;
  },

  async create(data: Partial<Insurer>): Promise<Insurer> {
    const response = await api.post('/insurers/insurers/', data);
    return response.data;
  },

  async update(id: string, data: Partial<Insurer>): Promise<Insurer> {
    const response = await api.patch(`/insurers/insurers/${id}/`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/insurers/insurers/${id}/`);
  },

  async activate(id: string): Promise<Insurer> {
    const response = await api.post(`/insurers/insurers/${id}/activate/`);
    return response.data;
  },

  async pause(id: string): Promise<Insurer> {
    const response = await api.post(`/insurers/insurers/${id}/pause/`);
    return response.data;
  },

  async block(id: string): Promise<Insurer> {
    const response = await api.post(`/insurers/insurers/${id}/block/`);
    return response.data;
  },

  async lookup(): Promise<{ id: string; name: string; code: string }[]> {
    const response = await api.get('/insurers/insurers/lookup/');
    return response.data;
  },

  async stats(): Promise<{ total: number; active: number; inactive: number }> {
    const response = await api.get('/insurers/insurers/report/');
    const { total, active, inactive } = response.data;
    return { total, active, inactive };
  },

  async createUser(
    insurerId: string,
    data: {
      first_name: string;
      last_name?: string;
      email: string;
      phone?: string;
      password: string;
      role: 'ADMIN' | 'MANAGER' | 'STAFF';
    }
  ): Promise<{ message: string; user_id: string; employee_id: string }> {
    const response = await api.post(`/insurers/insurers/${insurerId}/create_user/`, data);
    return response.data;
  },

  // Employees
  async listEmployees(params?: { insurer?: string }): Promise<PaginatedResponse<InsurerEmployee>> {
    const response = await api.get('/insurers/employees/', { params });
    return response.data;
  },

  async createEmployee(data: Partial<InsurerEmployee>): Promise<InsurerEmployee> {
    const response = await api.post('/insurers/employees/', data);
    return response.data;
  },

  async updateEmployee(id: string, data: Partial<InsurerEmployee>): Promise<InsurerEmployee> {
    const response = await api.patch(`/insurers/employees/${id}/`, data);
    return response.data;
  },

  async deleteEmployee(id: string): Promise<void> {
    await api.delete(`/insurers/employees/${id}/`);
  },

  // API Configs
  async listAPIConfigs(params?: { insurer?: string }): Promise<PaginatedResponse<InsurerAPIConfig>> {
    const response = await api.get('/insurers/api-configs/', { params });
    return response.data;
  },

  async createAPIConfig(data: Partial<InsurerAPIConfig>): Promise<InsurerAPIConfig> {
    const response = await api.post('/insurers/api-configs/', data);
    return response.data;
  },

  async updateAPIConfig(id: string, data: Partial<InsurerAPIConfig>): Promise<InsurerAPIConfig> {
    const response = await api.patch(`/insurers/api-configs/${id}/`, data);
    return response.data;
  },

  async deleteAPIConfig(id: string): Promise<void> {
    await api.delete(`/insurers/api-configs/${id}/`);
  },

  // -------------------------------------------------------------------------
  // Broker-scoped insurer surfaces (B10)
  //
  // `/insurers/insurers/` above is the COUNTRY ADMIN's catalogue -- every
  // insurer on the platform. Everything below is scoped on the server to one
  // broker, and each takes an optional `brokerId` so a multi-broker user's
  // BrokerSwitcher selection is honoured: the server pins one broker from
  // `tenant_id` otherwise, and REFUSES (403) a broker the caller does not
  // belong to rather than quietly answering with the pinned one.
  // -------------------------------------------------------------------------

  /** The broker's ACTIVE insurers, id/name/code only -- for filter dropdowns. */
  async brokerInsurers(brokerId?: string): Promise<BrokerInsurerOption[]> {
    const response = await api.get<BrokerInsurerOption[]>('/brokers/my-insurance-companies/', {
      params: brokerId ? { broker: brokerId } : undefined,
    });
    return response.data;
  },

  /** Every active insurer in the broker's country, with this broker's own
   *  relation status, connection request and policy counts. */
  async brokerInsurerManagement(brokerId?: string): Promise<BrokerInsurerRow[]> {
    const response = await api.get<BrokerInsurerRow[]>('/brokers/insurer-management/', {
      params: brokerId ? { broker: brokerId } : undefined,
    });
    return response.data;
  },

  /** Pause / resume an EXISTING connection. It cannot create one.
   *
   *  `brokerId` matters here for the same reason it does on the reads: without
   *  it the server pins the broker from `tenant_id`, so a switched user would
   *  disable the connection of a broker they are not looking at. */
  async toggleBrokerInsurer(
    insurerId: string,
    enable: boolean,
    brokerId?: string,
  ): Promise<{ status: string }> {
    const response = await api.post('/brokers/insurer-management/toggle_insurer/', {
      insurer_id: insurerId,
      enable,
      ...(brokerId ? { broker: brokerId } : {}),
    });
    return response.data;
  },

  /** Ask the country admin to connect this broker to an insurer. */
  async requestInsurerConnection(
    insurerId: string,
    note?: string,
    brokerId?: string,
  ): Promise<InsurerConnectionRequest> {
    const response = await api.post<InsurerConnectionRequest>(
      '/brokers/insurer-management/request_connection/',
      { insurer_id: insurerId, note: note ?? '', ...(brokerId ? { broker: brokerId } : {}) },
    );
    return response.data;
  },

  /** Country admin: the queue of brokers waiting to be connected. */
  async listInsurerConnectionRequests(params?: {
    status?: string;
    broker?: string;
  }): Promise<InsurerConnectionRequest[]> {
    const response = await api.get<{ count: number; results: InsurerConnectionRequest[] }>(
      '/brokers/insurer-connection-requests/',
      { params },
    );
    return response.data.results ?? [];
  },

  /** Country admin: approve, which is what creates the ACTIVE relation. */
  async approveInsurerConnectionRequest(id: string): Promise<InsurerConnectionRequest> {
    const response = await api.post<InsurerConnectionRequest>(
      `/brokers/insurer-connection-requests/${id}/approve/`,
    );
    return response.data;
  },

  async declineInsurerConnectionRequest(
    id: string,
    reason?: string,
  ): Promise<InsurerConnectionRequest> {
    const response = await api.post<InsurerConnectionRequest>(
      `/brokers/insurer-connection-requests/${id}/decline/`,
      { reason: reason ?? '' },
    );
    return response.data;
  },
};

export default insurersService;
